import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { aiFailure } from '../../utils/aiIdentify';
import { LAYERS } from '../ui/layers';
import ActionBar from '../ui/ActionBar';
import Button from '../ui/Button';
import Dialog from '../ui/Dialog';

/** The server's refusals that a person can act on, each in its own words. */
const REFUSALS = new Set([
  'photo_not_eligible', 'species_not_in_dictionary', 'species_only', 'taxon_required', 'already_a_reference',
  'species_full', 'store_full', 'model_mismatch', 'photo_not_found', 'photo_not_in_storage', 'embeddings_not_served',
]);

/**
 * Make a lab photograph a vetted reference of a species (L12; POST /references,
 * reference:vet). The person says which species it shows; the server checks the
 * photograph may be one, asks the model for its vector and keeps who vouched for
 * it. It can be withdrawn with a reason, never edited.
 *
 * `taxon` fills the name in to check, as the person is the one vouching for it.
 * This is no identification of the record: the isolate's name doesn't change.
 */
export default function ReferenceVetDialog({ photoId, taxon = '', onClose, onDone, testId = 'reference-vet' }) {
  const { t } = useTranslation('ai');
  const [name, setName] = useState(taxon || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post('/references', { photo_id: photoId, taxon_name: name.trim() });
      setDone(data);
      onDone?.(data);
    } catch (e) {
      const code = e?.response?.data?.code;
      if (REFUSALS.has(code)) {
        const reasons = e.response.data.reasons;
        setError(reasons?.length
          ? reasons.map((r) => t(`references.vet.reason.${r}`, { defaultValue: r })).join(' ')
          : t(`references.vet.refused.${code}`));
      } else {
        setError(t(aiFailure(e).key));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={t('references.vet.title')} onClose={onClose} busy={busy} dirty={!done && name.trim() !== (taxon || '').trim()}
      layer={LAYERS.overViewer} width={440}>
      {done ? (
        <>
          <p role="status" style={st.text} data-testid={`${testId}-done`}>
            {t('references.vet.done', { taxon: done.taxon_name })}
          </p>
          <ActionBar>
            <Button variant="primary" onClick={onClose} data-testid={`${testId}-close`}>{t('references.close')}</Button>
          </ActionBar>
        </>
      ) : (
        <>
          <p style={st.text}>{t('references.vet.intro')}</p>
          <label style={st.label}>
            {t('references.vet.species')}
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={256} dir="ltr" disabled={busy}
              style={st.field} data-testid={`${testId}-species`} />
          </label>
          {error && <p role="alert" style={st.error} data-testid={`${testId}-error`}>{error}</p>}
          <ActionBar>
            <Button onClick={onClose} disabled={busy}>{t('references.cancel')}</Button>
            <Button variant="primary" onClick={send} disabled={!name.trim() || busy} busy={busy}
              data-testid={`${testId}-save`}>
              {t('references.vet.save')}
            </Button>
          </ActionBar>
        </>
      )}
    </Dialog>
  );
}

const st = {
  text: { margin: '0 0 10px', fontSize: 13, color: 'var(--c-text-body-fg)' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--c-text-body-fg)', marginBottom: 10 },
  field: {
    display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 4, padding: 8, fontSize: 14,
    border: '1px solid var(--c-border-strong-2)', borderRadius: 6, background: 'var(--c-surface)', color: 'inherit',
  },
  error: { margin: '0 0 10px', fontSize: 13, color: 'var(--c-danger-3-fg)' },
};
